import React, { useState, useRef, useMemo } from 'react';
import { CollectionPropertyValue } from '../RealtimeCollectionProvider';
import { Calendar } from 'react-feather';
import { DayPicker } from 'react-day-picker';
import Dropdown from '../popover/Dropdown';
import { DateTime } from 'luxon';
import classNames from 'classnames';
import { DateUtils } from '@saga/shared'; // Assuming DateUtils is available
import i18next from 'i18next';
import { useUserContext } from '../UserContext';
import useMobile from '../../hooks/useMobile';
import Button from '../styled/Button';
import { useTranslation } from 'react-i18next';

const mobileStyles = {
    root: { margin: '8px' },
    cell: { width: '35px', height: '35px' },
    day: { width: '35px', height: '35px' },
};

function CollectionPropertyDateSelect({
    property,
    onChangeValue,
    showEmptyState = false,
    disabled = false,
}: {
    property: CollectionPropertyValue;
    onChangeValue: (value: string | undefined) => void;
    showEmptyState?: boolean;
    disabled?: boolean;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const divRef = useRef<HTMLDivElement>(null);
    const { userSettings } = useUserContext();
    const isMobile = useMobile();
    const { t } = useTranslation();

    // Validate and memoize parsed date values
    const parsedDateTime = useMemo(() => {
        if (!property.value) return null;
        const dt = DateTime.fromISO(property.value);
        return dt.isValid ? dt : null;
    }, [property.value]);

    const selectedJsDate = useMemo(() => {
        return parsedDateTime ? parsedDateTime.toJSDate() : undefined;
    }, [parsedDateTime]);

    const handleDateSelect = (date: Date | undefined) => {
        if (date) {
            onChangeValue(DateTime.fromJSDate(date).toISODate());
        }
        setIsOpen(false);
    };

    const handleRemoveDate = () => {
        onChangeValue(undefined);
        setIsOpen(false);
    };

    const renderDropdownContent = () => (
        <div className="pb-1 pt-2">
            <DayPicker
                weekStartsOn={userSettings?.weekStartOnMonday ? 1 : 0}
                mode="single"
                selected={selectedJsDate}
                defaultMonth={selectedJsDate ?? new Date()}
                onDayClick={handleDateSelect}
                styles={isMobile ? mobileStyles : undefined}
            />
            {selectedJsDate && (
                <div className="px-2 pt-2 border-t border-saga-gray-100 dark:border-saga-gray-800">
                    <Button
                        variant="secondary"
                        onClick={handleRemoveDate}
                        className="w-full justify-center text-sm py-1.5"
                    >
                        {t('collections.properties.remove_date')}
                    </Button>
                </div>
            )}
        </div>
    );

    if (!parsedDateTime && showEmptyState) {
        return (
            <div ref={divRef} className="hover-trigger">
                <Button.DashedPill disabled={disabled} onClick={() => !disabled && setIsOpen(true)}>
                    {t('collections.properties.select_date')}
                </Button.DashedPill>
                {isOpen && (
                    <Dropdown
                        testId="date-property-picker-empty"
                        isOpen={isOpen}
                        attachToRef={divRef}
                        onClose={() => setIsOpen(false)}
                        align={isMobile ? 'right' : 'left'}
                    >
                        {renderDropdownContent()}
                    </Dropdown>
                )}
            </div>
        );
    }

    return (
        <div ref={divRef} className="hover-trigger flex items-center text-sm">
            <button
                type="button"
                disabled={disabled}
                className={classNames(
                    'whitespace-nowrap text-saga-text dark:text-neutral-200 font-medium relative select-none rounded space-x-1 cursor-pointer px-1 py-0.5 min-h-6 flex items-center',
                    {
                        'hover:bg-saga-gray-200 dark:hover:bg-zinc-200/10': !disabled,
                        'w-full': !parsedDateTime && !showEmptyState,
                    },
                )}
                onClick={() => !disabled && setIsOpen(true)}
                data-testid="date-property-display"
            >
                {parsedDateTime && (
                    <>
                        <Calendar
                            className="inline-block align-text-bottom select-none mr-1 flex-shrink-0"
                            style={{ height: '1.2em', width: '1em' }}
                        />
                        <span>{DateUtils.formatDateBlockDate(parsedDateTime, i18next.language)}</span>
                    </>
                )}
            </button>
            {isOpen && (
                <Dropdown
                    testId="date-property-picker"
                    isOpen={isOpen}
                    attachToRef={divRef}
                    onClose={() => setIsOpen(false)}
                    align={isMobile ? 'right' : 'left'}
                >
                    {renderDropdownContent()}
                </Dropdown>
            )}
        </div>
    );
}

export default CollectionPropertyDateSelect;
